import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Landmark, Save } from "lucide-react";
import { SectionCard } from "../../../components/ui/SectionCard";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Button } from "../../../components/ui/Button";
import { Skeleton } from "../../../components/ui/Skeleton";
import { useCountry } from "../../../context/CountryContext";
import { banksForCountry } from "../../adminView/settings/sections/banks";
import { updateMyBankData } from "../../../api/user";
import { showToast } from "../../../utils/showToast";
import {
  apiBodyMessage,
  apiBodyMessages,
  mapValidationErrors,
} from "../../../utils/apiErrorMessage";
import { OTHER_BANK, formConfigForCountry, initialForm } from "./bankFormConfig";

// Length caps mirroring UpdateBankDataDto in microservice-users (@MaxLength).
// Enforced here so the user gets inline feedback instead of a wasted PUT; the
// backend revalidates anyway.
const MAX_LENGTH = { name: 120, rut: 20, bank: 80, accountType: 40, accountNumber: 34 };

// Sección "Datos bancarios" de Mi Cuenta. Solo la ven los usuarios marcados
// como VENDEDOR por el operador (flag `isSeller`): es la cuenta a la que se le
// transfiere (liquidaciones COD, comisiones), no un dato de perfil más.
//
// El formulario se adapta al país de operación (CountryContext) según
// `bankFormConfig.js`: nómina de bancos, tipos de cuenta, etiqueta y validación
// del documento del titular. Se guarda COMPLETO en un solo PUT (el backend
// reemplaza el subdocumento entero), por eso hay un único botón Guardar y no
// edición campo a campo como el resto de la página.

export default function BankDataSection({ bankData, onSaved }) {
  const { t } = useTranslation();
  const { countryCode, loading: countryLoading } = useCountry();

  // El contexto arranca en Chile mientras resuelve el país real: si el form se
  // inicializara en ese estado, un banco argentino guardado caería en "Otro
  // banco" por no estar en la nómina chilena. Se espera la resolución y se
  // remonta el form (key) si el código cambiara.
  if (countryLoading) {
    return (
      <SectionCard
        icon={<Landmark className="w-4 h-4" />}
        title={t("Datos bancarios")}
        subtitle={t("La cuenta a la que te transferirá tu operador.")}
      >
        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      </SectionCard>
    );
  }

  return (
    <BankDataForm
      key={countryCode}
      countryCode={countryCode}
      bankData={bankData}
      onSaved={onSaved}
    />
  );
}

function BankDataForm({ countryCode, bankData, onSaved }) {
  const { t } = useTranslation();

  const cfg = formConfigForCountry(countryCode);
  const bankOptions = useMemo(
    () => banksForCountry(countryCode),
    [countryCode],
  );

  const [form, setForm] = useState(() => initialForm(bankData, bankOptions));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const clearError = (key) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  };

  // El texto libre del banco se escribe en `bankOther` pero su error vive bajo
  // la clave `bank` (es un solo campo lógico). Sin este handler, corregir el
  // nombre del banco no limpiaba el borde rojo ni el mensaje.
  const setBankOther = (value) => {
    setForm((prev) => ({ ...prev, bankOther: value, bank: OTHER_BANK }));
    clearError("bank");
  };

  const maxLengthMessage = (limit) => t("Máximo {{n}} caracteres", { n: limit });

  // The backend answers in English (class-validator); the field messages are
  // shown in Spanish. Any other shape falls back to a generic message instead
  // of leaking the raw text.
  const localizeValidationMessage = (message) => {
    const text = String(message ?? "");
    const maxLength = /shorter than or equal to (\d+) characters/.exec(text);
    if (maxLength) return maxLengthMessage(maxLength[1]);
    if (/should not be empty/.test(text)) return t("Este campo es obligatorio");
    return t("Valor no válido");
  };

  const validate = () => {
    const errs = {};
    const name = form.name.trim();
    if (name.length < 3) {
      errs.name = t("Ingresa el nombre del titular");
    } else if (name.length > MAX_LENGTH.name) {
      errs.name = maxLengthMessage(MAX_LENGTH.name);
    }
    const rut = form.rut.trim();
    if (!cfg.docValidate(rut)) {
      errs.rut = t(cfg.docError);
    } else if (rut.length > MAX_LENGTH.rut) {
      errs.rut = maxLengthMessage(MAX_LENGTH.rut);
    }
    const bankValue = (form.bank === OTHER_BANK ? form.bankOther : form.bank).trim();
    if (!bankValue) {
      errs.bank =
        form.bank === OTHER_BANK
          ? t("Ingresa el nombre del banco")
          : t("Selecciona el banco");
    } else if (bankValue.length > MAX_LENGTH.bank) {
      errs.bank = maxLengthMessage(MAX_LENGTH.bank);
    }
    if (!form.accountType) {
      errs.accountType = t("Selecciona el tipo de cuenta");
    }
    const accountNumber = form.accountNumber.trim();
    if (!cfg.accountValidate(accountNumber)) {
      errs.accountNumber = t(cfg.accountError);
    } else if (accountNumber.length > MAX_LENGTH.accountNumber) {
      errs.accountNumber = maxLengthMessage(MAX_LENGTH.accountNumber);
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        rut: form.rut.trim(),
        bank: (form.bank === OTHER_BANK ? form.bankOther : form.bank).trim(),
        accountType: form.accountType,
        accountNumber: form.accountNumber.trim(),
      };
      const { data, error } = await updateMyBankData(payload);
      if (error) {
        // `error` is the response body. A Nest ValidationPipe array is mapped
        // back onto the form fields so each one is marked in red with its
        // message in Spanish; anything else (404, empty body) is shown as the
        // backend sent it.
        const { errors: fieldErrors } = mapValidationErrors(apiBodyMessages(error), {
          name: "name",
          rut: "rut",
          bank: "bank",
          accountType: "accountType",
          accountNumber: "accountNumber",
        });
        const keys = Object.keys(fieldErrors);
        if (keys.length) {
          const localized = {};
          keys.forEach((key) => {
            localized[key] = localizeValidationMessage(fieldErrors[key]);
          });
          setErrors((prev) => ({ ...prev, ...localized }));
          // Messages that did not map to a field are dropped on purpose: they
          // arrive in English and the toast already tells the user what to do.
          showToast(t("Revisa los campos marcados"), false);
          return;
        }
        showToast(
          apiBodyMessage(error) ?? t("No se pudieron guardar los datos bancarios"),
          false,
        );
        return;
      }
      showToast(t("Datos bancarios guardados"), true);
      onSaved?.(data?.bankData || payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={<Landmark className="w-4 h-4" />}
      title={t("Datos bancarios")}
      subtitle={t("La cuenta a la que te transferirá tu operador.")}
      action={
        <Button
          size="sm"
          iconLeft={<Save className="w-4 h-4" />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t("Guardando…") : t("Guardar")}
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <FieldLabel>{t("Nombre del titular")}</FieldLabel>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder={t("Nombre y apellido del titular")}
            invalid={!!errors.name}
            maxLength={MAX_LENGTH.name}
            autoComplete="off"
          />
          {errors.name && <p className="text-xs text-status-error">{errors.name}</p>}
        </div>

        <div className="space-y-1">
          <FieldLabel>{t(cfg.docLabel)}</FieldLabel>
          <Input
            value={form.rut}
            onChange={(e) => setField("rut", e.target.value)}
            placeholder={t(cfg.docPlaceholder)}
            invalid={!!errors.rut}
            maxLength={MAX_LENGTH.rut}
            autoComplete="off"
            className="font-mono"
          />
          {errors.rut && <p className="text-xs text-status-error">{errors.rut}</p>}
        </div>

        <div className="space-y-1">
          <FieldLabel>{t("Banco")}</FieldLabel>
          {bankOptions?.length ? (
            <>
              <Select
                value={form.bank}
                onChange={(e) => setField("bank", e.target.value)}
                placeholder={t("Selecciona tu banco")}
                // Con "Otro banco" el mensaje se muestra junto al input de
                // texto libre, que es el control que hay que corregir.
                error={form.bank === OTHER_BANK ? undefined : errors.bank}
              >
                {bankOptions.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
                <option value={OTHER_BANK}>{t("Otro banco (especificar)")}</option>
              </Select>
              {form.bank === OTHER_BANK && (
                <>
                  <Input
                    value={form.bankOther}
                    onChange={(e) => setBankOther(e.target.value)}
                    placeholder={t("Nombre del banco")}
                    invalid={!!errors.bank}
                    maxLength={MAX_LENGTH.bank}
                    autoComplete="off"
                  />
                  {errors.bank && (
                    <p className="text-xs text-status-error">{errors.bank}</p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Input
                value={form.bankOther}
                onChange={(e) => setBankOther(e.target.value)}
                placeholder={t("Nombre del banco")}
                invalid={!!errors.bank}
                maxLength={MAX_LENGTH.bank}
                autoComplete="off"
              />
              {errors.bank && <p className="text-xs text-status-error">{errors.bank}</p>}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <FieldLabel>{t("Tipo de cuenta")}</FieldLabel>
            <Select
              value={form.accountType}
              onChange={(e) => setField("accountType", e.target.value)}
              placeholder={t("Selecciona")}
              error={errors.accountType}
            >
              {cfg.accountTypes.map((type) => (
                <option key={type} value={type}>{t(type)}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <FieldLabel>{t("Número de cuenta")}</FieldLabel>
            <Input
              value={form.accountNumber}
              onChange={(e) => setField("accountNumber", e.target.value)}
              placeholder={t(cfg.accountPlaceholder)}
              invalid={!!errors.accountNumber}
              maxLength={MAX_LENGTH.accountNumber}
              autoComplete="off"
              className="font-mono"
            />
            {errors.accountNumber && (
              <p className="text-xs text-status-error">{errors.accountNumber}</p>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
